import type { ReactNode } from "react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { getFooter } from "@/services/uhfService";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import { isValidLocaleSegment, normalizeLocaleForTranslations } from "@/utils/localeRouteHelpers";
import { emitThemeChanged } from "../UniversalHeader/helpers/CookieConsentHelpers";
import styles from "./UniversalFooter.module.scss";

interface ICookieSiteConsent {
  manageConsent: () => void;
}

interface ICookieConsentWindow extends Window {
  mciCookieSiteConsent?: ICookieSiteConsent;
}

const manageCookiesLinkId = "manage-cookies-link";
const uhfManageCookiesSelector = "#uhf-Footer_ManageCookies, #c-uhff-footer_managecookies";
const manageCookiesDialogOpenedEvent = "mci-cookie-consent-dialog-opened";

function openCookieConsentDialog(): void {
  const windowObj = window as ICookieConsentWindow;

  if (!windowObj.mciCookieSiteConsent) {
    console.warn("[CookieConsent] Cookie consent is not initialized yet");
    return;
  }

  window.dispatchEvent(new CustomEvent(manageCookiesDialogOpenedEvent));
  emitThemeChanged();
  windowObj.mciCookieSiteConsent.manageConsent();
}

function addManageCookiesLink(footerElement: HTMLElement): void {
  if (footerElement.querySelector(`#${manageCookiesLinkId}`)) {
    return;
  }

  const manageCookiesElement = footerElement.querySelector<HTMLElement>(uhfManageCookiesSelector);
  if (manageCookiesElement) {
    manageCookiesElement.closest("li")?.classList.remove("hidden", "x-hidden");
    manageCookiesElement.classList.remove("hidden", "x-hidden");

    const existingLink = manageCookiesElement.matches("a")
      ? (manageCookiesElement as HTMLAnchorElement)
      : manageCookiesElement.querySelector<HTMLAnchorElement>('a[data-mscc-ic="false"]');

    if (existingLink) {
      existingLink.id = manageCookiesLinkId;
      existingLink.addEventListener("click", (event: MouseEvent): void => {
        event.preventDefault();
        openCookieConsentDialog();
      });
      return;
    }
  }
}

function applyFooterTheme(footerHtml: string): string {
  const template = document.createElement("template");
  template.innerHTML = footerHtml;

  const theme = document.documentElement.classList.contains("theme-dark") ? "dark" : "light";
  template.content.querySelector("uhf-footer")?.setAttribute("theme", theme);

  return template.innerHTML;
}

/**
 * Microsoft Universal Footer Component
 * Loads and renders the Microsoft UHF footer
 */
export function UniversalFooter(): ReactNode {
  const params = useParams();
  const routeLocale = normalizeLocaleForTranslations(params?.locale as string | undefined);
  const storedLocale = normalizeLocaleForTranslations(getLocalePreference()?.localeCode);

  const localeCode =
    (routeLocale && isValidLocaleSegment(routeLocale) ? routeLocale : undefined) ??
    (storedLocale && isValidLocaleSegment(storedLocale) ? storedLocale : undefined) ??
    "en-us";
  
  useEffect(() => {
    const footer = document.getElementById("footer");
    if (!footer) {
      console.error("Footer element not found");
      return;
    }
    const footerElement = footer;

    const footerObserver = new MutationObserver((): void => {
      addManageCookiesLink(footerElement);
    });
    footerObserver.observe(footerElement, { childList: true, subtree: true });

    let isEffectDisposed = false;

    async function parseAndLoadUHF(): Promise<void> {
      try {
        const footerHtml = await getFooter(localeCode);

        if (!footerHtml) {
          console.error("Failed to load UHF footer data");
          return;
        }

        if (isEffectDisposed) {
          return;
        }

        const footerSection = document.getElementById("footer-container");

        if (!footerSection) {
          console.error("Footer container not found");
          return;
        }

        footerElement.innerHTML = applyFooterTheme(footerHtml);
        addManageCookiesLink(footerElement);
      } catch (error) {
        console.error("Error loading UHF footer:", error);
      }
    }

    void parseAndLoadUHF();

    return (): void => {
      isEffectDisposed = true;
      footerObserver.disconnect();
    };
  }, [localeCode]);

  return (
    <div id="footer-container" className={styles.footerContainer}>
      <div id="footer" role="contentinfo" />
    </div>
  );
}
